import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Box,
  IconButton,
  Divider,
  useTheme
} from '@mui/material';
import {
  AccountBalance,
  Person,
  CreditCard,
  TrendingUp,
  MoreVert,
  Notifications
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const RecentActivities = () => {
  const theme = useTheme();
  const activities = [
    {
      id: 1,
      type: 'loan_approved',
      title: 'Loan Application Approved',
      description: 'John Doe - Personal Loan ₹2,50,000',
      time: '2 hours ago',
      icon: CreditCard,
      color: 'success',
      status: 'approved'
    },
    {
      id: 2,
      type: 'member_joined',
      title: 'New Member Registration',
      description: 'Sarah Wilson joined as Premium Member',
      time: '4 hours ago',
      icon: Person,
      color: 'primary',
      status: 'new'
    },
    {
      id: 3,
      type: 'payment_received',
      title: 'Payment Received',
      description: 'Monthly installment ₹15,000 from Mike Johnson',
      time: '6 hours ago',
      icon: AccountBalance,
      color: 'success',
      status: 'completed'
    },
    {
      id: 4,
      type: 'interest_calculated',
      title: 'Interest Calculation',
      description: 'Monthly interest calculated for 45 accounts',
      time: '1 day ago',
      icon: TrendingUp,
      color: 'info',
      status: 'processed'
    },
    {
      id: 5,
      type: 'reminder_sent',
      title: 'Payment Reminders Sent',
      description: '12 overdue payment reminders dispatched',
      time: '1 day ago',
      icon: Notifications,
      color: 'warning',
      status: 'sent'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
      case 'completed':
        return 'success';
      case 'new':
        return 'primary';
      case 'processed':
        return 'info';
      case 'sent':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <Card
        sx={{
          height: '100%',
          borderRadius: 3,
          backgroundColor: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.04)'
            : 'rgba(0, 0, 0, 0.02)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (theme) => theme.palette.mode === 'dark'
            ? '0px 4px 12px rgba(0, 0, 0, 0.3)'
            : '0px 2px 8px rgba(0, 0, 0, 0.08)',
        }}
      >
        <CardHeader
          title={
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
              Recent Activities
            </Typography>
          }
          action={
            <IconButton
              size="small"
              sx={{
                color: 'text.secondary',
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'rgba(0, 0, 0, 0.08)'
                }
              }}
            >
              <MoreVert />
            </IconButton>
          }
          sx={{ pb: 1 }}
        />
        <CardContent sx={{ pt: 0, maxHeight: 400, overflow: 'auto' }}>
          <List disablePadding>
            {activities.map((activity, index) => {
              const IconComponent = activity.icon;
              return (
                <motion.div
                  key={activity.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      px: 0,
                      py: 1.5,
                      '&:hover': {
                        backgroundColor: (theme) => theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.04)'
                          : 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 2,
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: (theme) => theme.palette.mode === 'dark'
                            ? `${activity.color}.main` + '20'
                            : `${activity.color}.main` + '15',
                          color: `${activity.color}.main`,
                          width: 40,
                          height: 40
                        }}
                      >
                        <IconComponent fontSize="small" />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {activity.title}
                          </Typography>
                          <Chip
                            label={activity.status}
                            size="small"
                            color={getStatusColor(activity.status)}
                            variant="outlined"
                            sx={{
                              fontSize: '0.75rem',
                              height: 20,
                              borderColor: `${getStatusColor(activity.status)}.main`,
                              color: `${getStatusColor(activity.status)}.main`,
                              backgroundColor: (theme) => theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.04)'
                                : 'rgba(0, 0, 0, 0.02)'
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {activity.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {activity.time}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < activities.length - 1 && <Divider variant="inset" component="li" />}
                </motion.div>
              );
            })}
          </List>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default RecentActivities;